import React, { FC, useEffect, useReducer } from 'react';

import { Box, HStack } from '@chakra-ui/react';
import { map } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';

import { FilterLabelWrapper } from 'components/DataDisplay/Box/BorderedBox/FilterLabelWrapper';
import { Select } from 'components/DataEntry';
import { SearchBox } from 'components/DataEntry/Input/SearchBox';
import { useResourceType } from 'containers/App/hooks/useResourceType';
import { severityOptions } from 'containers/Inventory/AllowList/Components/AllowListWizard/Steps/AllowListCriteria/ResourceTypeCriteria/utils';
import { policyTypeOptions } from 'containers/PolicyHub/Components/PoliciesTableHeader/utils';
import { selectPolicyHub } from 'containers/PolicyHub/selectors';
import { actions as policyHubActions } from 'containers/PolicyHub/slice';
import { toTitleCase } from 'utils/string';

interface Props {
  action?: 'Create' | 'Show' | 'Add';
  compliance?: string | null;
  createdBy?: string;
  resourceType: Record<string, any>;
  alertCategory: Record<string, any>;
  policyType: Record<string, any>;
  severity: Record<string, any>;
  updateValue(field: string, value: any);
}

export const PoliciesTableHeader: FC<Props> = props => {
  const {
    compliance = '',
    createdBy = '',
    resourceType,
    updateValue,
    alertCategory,
    policyType,
    severity,
  } = props;
  const dispatch = useDispatch();
  const { alertCategories } = useSelector(selectPolicyHub);
  const { resourceTypeAliasOptionsWithAll } = useResourceType();

  const [state, updateState] = useReducer(
    (prev, next) => {
      return { ...prev, ...next };
    },
    {
      alertCategoryOptions: [],
      resourceTypeOptions: [],
      severityOptions: [
        { label: 'Severity', value: undefined },
        ...severityOptions,
      ],
      searchTerm: '',
    },
  );

  //get alert categories
  useEffect(() => {
    dispatch(policyHubActions.getAlertCategories({ q: {} }));
  }, [dispatch]);

  //set resource type options
  useEffect(() => {
    const options = resourceTypeAliasOptionsWithAll;
    updateState({
      resourceTypeOptions: options,
    });
    updateValue('resourceType', options[0]);
  }, [resourceTypeAliasOptionsWithAll, updateValue]);

  //set alert category options
  useEffect(() => {
    const options = [
      { label: 'Alert Category', value: '' },
      ...map(alertCategories.data, o => ({
        label: toTitleCase(o),
        value: o,
      })),
    ];
    updateState({
      alertCategoryOptions: options,
    });
    updateValue('alertCategory', options[0]);
  }, [alertCategories.data, updateValue]);

  return (
    <HStack w="full" spacing={4}>
      <Box w="30%">
        <SearchBox
          value={state.searchTerm}
          onChange={s => {
            updateState({ searchTerm: s });
            if (!s) updateValue('searchTerm', '');
          }}
          onSearch={() => updateValue('searchTerm', state.searchTerm)}
          onClickClose={() => {
            updateState({ searchTerm: '' });
            updateValue('searchTerm', '');
          }}
        />
      </Box>
      {!compliance && (
        <HStack>
          <FilterLabelWrapper>FILTER BY</FilterLabelWrapper>
          <Box zIndex={900}>
            <Select
              options={policyTypeOptions}
              value={policyType}
              onChange={s => updateValue('policyType', s)}
              showIconInValueContainer
              isAFilter
              leftMessage="Policy Type"
              showUndefinedValueLabel={false}
            />
          </Box>
          <Box zIndex={900}>
            <Select
              options={state.severityOptions}
              value={severity}
              onChange={s => updateValue('severity', s)}
              showIconInValueContainer
              isAFilter
              leftMessage="Severity"
              showUndefinedValueLabel={false}
            />
          </Box>
          <Box zIndex={900}>
            <Select
              options={state.alertCategoryOptions}
              value={alertCategory}
              onChange={s => updateValue('alertCategory', s)}
              isLoading={alertCategories.isLoading}
              showIconInValueContainer
              isAFilter
              leftMessage="Alert Category"
              showUndefinedValueLabel={false}
            />
          </Box>
          <Box zIndex={900}>
            <Select
              options={state.resourceTypeOptions}
              value={resourceType}
              onChange={s => updateValue('resourceType', s)}
              showIconInValueContainer
              isAFilter
              leftMessage="Resource Type"
              showUndefinedValueLabel={false}
            />
          </Box>
        </HStack>
      )}
    </HStack>
  );
};
